import { useMemo, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowDown,
  ArrowUp,
  BarChart3,
  ChevronLeft,
  ClipboardList,
  Eye,
  EyeOff,
  ListChecks,
  Pencil,
  Plus,
  Trash2,
  X,
  type LucideIcon,
} from 'lucide-react';
import { Modale } from '../ui/Modale';
import { Chargement, MarqueObligatoire, MessageErreur, Pastille } from '../ui/Primitives';
import { useToast } from '../ui/useToast';
import { ErreurApi } from '../../lib/api';
import { echeanceLongue } from '../../lib/affichage-devoirs';
import {
  useDevoirsCours,
  useGesteDevoir,
  useResultatsDevoir,
  type Cours,
  type DevoirDuCours,
  type DevoirsDuCours,
  type EtatResultat,
  type GesteDevoir,
  type SaisieDevoir,
  type SaisieQuestion,
  type TypeDevoirCours,
} from '../../lib/useCours';

/**
 * Étape « Évaluations & quiz » de « Créer un cours » (TF-454) : les quiz et
 * les devoirs à rendre du cours, rangés par module, la fin du cours en
 * dernier. Chacun naît en brouillon ; publié, l'apprenant le voit une fois le
 * cours publié. Les questions d'un quiz s'écrivent ici, QCM à réponse unique.
 * Dès qu'un apprenant a commencé ou rendu, l'API fige questions et durée et
 * refuse retrait et suppression : chaque ligne porte ces refus, l'écran ne
 * juge rien. Les résultats disent où en est chaque apprenant ; la correction
 * viendra avec TF-457.
 */

const CLASSE_ACTION =
  'inline-flex min-h-[36px] items-center gap-1.5 rounded-lg px-2.5 text-xs font-semibold transition disabled:cursor-not-allowed disabled:opacity-50 [@media(pointer:coarse)]:min-h-[40px]';
const ACTION_PRINCIPALE = `${CLASSE_ACTION} bg-codialis-navy text-white hover:bg-codialis-deep`;
const ACTION_SECONDAIRE = `${CLASSE_ACTION} border border-gray-200 bg-white text-codialis-navy hover:bg-gray-50`;
const ACTION_ICONE =
  'inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-codialis-gray transition hover:bg-gray-100 hover:text-codialis-navy disabled:cursor-not-allowed disabled:opacity-40 [@media(pointer:coarse)]:h-10 [@media(pointer:coarse)]:w-10';
const CLASSE_CHAMP =
  'w-full min-w-0 rounded-xl border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-codialis-navy focus:border-transparent focus:outline-none focus:ring-2 focus:ring-codialis-mint';
const CLASSE_LIBELLE = 'mb-1 block text-xs font-semibold uppercase text-codialis-navy';
const BOUTON_PRINCIPAL =
  'min-h-[44px] rounded-xl bg-codialis-navy px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-codialis-deep disabled:opacity-60';
const BOUTON_SECONDAIRE =
  'min-h-[44px] rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-sm font-medium text-codialis-navy hover:bg-gray-50 disabled:opacity-60';

const ICONES: Record<'QUIZ' | 'DEVOIR', LucideIcon> = { QUIZ: ListChecks, DEVOIR: ClipboardList };

const TYPES_A_RENDRE: { valeur: Exclude<TypeDevoirCours, 'QUIZ'>; libelle: string }[] = [
  { valeur: 'PROJET', libelle: 'Projet' },
  { valeur: 'RAPPORT', libelle: 'Rapport' },
  { valeur: 'AUTRE', libelle: 'Autre devoir' },
];

function messageErreur(erreur: unknown, defaut: string): string {
  return erreur instanceof ErreurApi ? erreur.message : defaut;
}

function noteLisible(note: number): string {
  return String(note).replace('.', ',');
}

function dateLocale(iso: string): string {
  return new Date(iso).toLocaleString('fr-FR', {
    timeZone: 'Indian/Reunion',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/** Demain, en date de La Réunion : la première échéance proposée. */
function demainALaReunion(): string {
  const demain = new Date(Date.now() + 86_400_000);
  return demain.toLocaleDateString('sv-SE', { timeZone: 'Indian/Reunion' });
}

// ---------------------------------------------------------------------------
// Formulaire d'un devoir
// ---------------------------------------------------------------------------

type Edition = { mode: 'creer'; quiz: boolean } | { mode: 'modifier'; devoir: DevoirDuCours };

function saisieInitiale(edition: Edition, vue: DevoirsDuCours): SaisieDevoir {
  if (edition.mode === 'modifier') {
    const { devoir } = edition;
    return {
      type: devoir.type,
      titre: devoir.titre,
      consigne: devoir.consigne,
      moduleId: devoir.module?.id ?? null,
      blocId: devoir.bloc.id,
      echeance: devoir.echeanceSaisie,
      dureeMinutes: devoir.dureeMinutes,
    };
  }
  return {
    type: edition.quiz ? 'QUIZ' : 'PROJET',
    titre: '',
    consigne: null,
    moduleId: vue.modules[0]?.id ?? null,
    blocId: vue.blocDuCours ?? vue.blocs[0]?.id ?? '',
    echeance: { date: demainALaReunion(), heure: vue.limites.heureParDefaut },
    dureeMinutes: edition.quiz ? vue.limites.dureeParDefaut : null,
  };
}

function FormulaireDevoir({
  vue,
  edition,
  onFermer,
  executer,
  enCours,
}: {
  vue: DevoirsDuCours;
  edition: Edition;
  onFermer: () => void;
  executer: (geste: GesteDevoir, succes: string) => Promise<boolean>;
  enCours: boolean;
}): JSX.Element {
  const [saisie, setSaisie] = useState<SaisieDevoir>(() => saisieInitiale(edition, vue));
  const quiz = saisie.type === 'QUIZ';
  const dureeFigee = edition.mode === 'modifier' ? edition.devoir.refus.questions : null;
  const typeFige = edition.mode === 'modifier' && edition.devoir.commences > 0;
  const { limites } = vue;

  const soumettre = async (evenement: FormEvent) => {
    evenement.preventDefault();
    const envoi = { ...saisie, titre: saisie.titre.trim(), consigne: saisie.consigne?.trim() || null };
    const fait =
      edition.mode === 'creer'
        ? await executer({ geste: 'creer', saisie: envoi }, quiz ? 'Quiz créé en brouillon' : 'Devoir créé en brouillon')
        : await executer({ geste: 'modifier', devoirId: edition.devoir.id, saisie: envoi }, 'Modifications enregistrées');
    if (fait) onFermer();
  };

  return (
    <form onSubmit={(evenement) => void soumettre(evenement)} className="space-y-4 p-4 sm:p-6">
      {!quiz && (
        <div>
          <label htmlFor="devoir-type" className={CLASSE_LIBELLE}>
            Nature du devoir
          </label>
          <select
            id="devoir-type"
            value={saisie.type}
            disabled={typeFige}
            onChange={(evenement) => setSaisie({ ...saisie, type: evenement.target.value as TypeDevoirCours })}
            className={CLASSE_CHAMP}
          >
            {TYPES_A_RENDRE.map((type) => (
              <option key={type.valeur} value={type.valeur}>
                {type.libelle}
              </option>
            ))}
          </select>
        </div>
      )}
      <div>
        <label htmlFor="devoir-titre" className={CLASSE_LIBELLE}>
          Titre <MarqueObligatoire className="text-red-500" />
        </label>
        <input
          id="devoir-titre"
          required
          maxLength={limites.titreMax}
          value={saisie.titre}
          onChange={(evenement) => setSaisie({ ...saisie, titre: evenement.target.value })}
          className={CLASSE_CHAMP}
        />
      </div>
      <div>
        <label htmlFor="devoir-consigne" className={CLASSE_LIBELLE}>
          Consigne
        </label>
        <textarea
          id="devoir-consigne"
          rows={3}
          maxLength={limites.consigneMax}
          value={saisie.consigne ?? ''}
          onChange={(evenement) => setSaisie({ ...saisie, consigne: evenement.target.value })}
          className={CLASSE_CHAMP}
        />
      </div>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div className="min-w-0">
          <label htmlFor="devoir-module" className={CLASSE_LIBELLE}>
            Module du cours
          </label>
          <select
            id="devoir-module"
            value={saisie.moduleId ?? ''}
            onChange={(evenement) => setSaisie({ ...saisie, moduleId: evenement.target.value || null })}
            className={CLASSE_CHAMP}
          >
            {vue.modules.map((module, rang) => (
              <option key={module.id} value={module.id}>
                Module {rang + 1} · {module.titre}
              </option>
            ))}
            <option value="">Fin du cours</option>
          </select>
        </div>
        <div className="min-w-0">
          <label htmlFor="devoir-bloc" className={CLASSE_LIBELLE}>
            Bloc du programme <MarqueObligatoire className="text-red-500" />
          </label>
          <select
            id="devoir-bloc"
            required
            value={saisie.blocId}
            onChange={(evenement) => setSaisie({ ...saisie, blocId: evenement.target.value })}
            aria-describedby="devoir-bloc-aide"
            className={CLASSE_CHAMP}
          >
            {vue.blocs.length === 0 && <option value="">Aucun bloc au programme</option>}
            {vue.blocs.map((bloc, rang) => (
              <option key={bloc.id} value={bloc.id}>
                Bloc {rang + 1} · {bloc.libelle}
              </option>
            ))}
          </select>
          <p id="devoir-bloc-aide" className="mt-1 text-[11px] text-codialis-gray">
            {quiz ? 'Le quiz compte dans la progression de ce bloc.' : 'Le devoir se range sous ce bloc.'}
          </p>
        </div>
      </div>
      <fieldset className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <legend className={`${CLASSE_LIBELLE} sm:col-span-3`}>Échéance (heure de La Réunion)</legend>
        <div className="min-w-0">
          <label htmlFor="devoir-date" className="sr-only">
            Date d’échéance
          </label>
          <input
            id="devoir-date"
            type="date"
            required
            value={saisie.echeance.date}
            onChange={(evenement) => setSaisie({ ...saisie, echeance: { ...saisie.echeance, date: evenement.target.value } })}
            className={CLASSE_CHAMP}
          />
        </div>
        <div className="min-w-0">
          <label htmlFor="devoir-heure" className="sr-only">
            Heure d’échéance
          </label>
          <input
            id="devoir-heure"
            type="time"
            required
            value={saisie.echeance.heure}
            onChange={(evenement) => setSaisie({ ...saisie, echeance: { ...saisie.echeance, heure: evenement.target.value } })}
            className={CLASSE_CHAMP}
          />
        </div>
        {quiz && (
          <div className="min-w-0">
            <label htmlFor="devoir-duree" className="sr-only">
              Durée du quiz en minutes
            </label>
            <div className="flex items-center gap-2">
              <input
                id="devoir-duree"
                type="number"
                required
                min={limites.dureeMin}
                max={limites.dureeMax}
                disabled={Boolean(dureeFigee)}
                value={saisie.dureeMinutes ?? ''}
                onChange={(evenement) =>
                  setSaisie({ ...saisie, dureeMinutes: evenement.target.value ? Number(evenement.target.value) : null })
                }
                className={CLASSE_CHAMP}
              />
              <span className="shrink-0 text-sm text-codialis-gray">min</span>
            </div>
          </div>
        )}
      </fieldset>
      {dureeFigee && <p className="text-xs text-codialis-gray">{dureeFigee}</p>}
      <p className="text-xs text-codialis-gray">
        La même échéance vaut pour toutes les promotions : un apprenant entré en formation après elle ne reçoit pas ce
        {quiz ? ' quiz' : ' devoir'}.
      </p>
      <div className="flex flex-wrap justify-end gap-2 pt-2">
        <button type="button" onClick={onFermer} className={BOUTON_SECONDAIRE}>
          Annuler
        </button>
        <button type="submit" disabled={enCours || !saisie.blocId} className={BOUTON_PRINCIPAL}>
          {edition.mode === 'creer' ? 'Créer en brouillon' : 'Enregistrer'}
        </button>
      </div>
    </form>
  );
}

// ---------------------------------------------------------------------------
// Questions d'un quiz
// ---------------------------------------------------------------------------

interface QuestionEditee extends SaisieQuestion {
  cle: number;
}

let prochaineCle = 1;

function questionVide(): QuestionEditee {
  return {
    cle: prochaineCle++,
    enonce: '',
    explication: null,
    reponses: [
      { texte: '', correcte: true },
      { texte: '', correcte: false },
    ],
  };
}

function EditeurQuiz({
  devoir,
  vue,
  onFermer,
  executer,
  enCours,
}: {
  devoir: DevoirDuCours;
  vue: DevoirsDuCours;
  onFermer: () => void;
  executer: (geste: GesteDevoir, succes: string) => Promise<boolean>;
  enCours: boolean;
}): JSX.Element {
  const { limites } = vue;
  const figees = devoir.refus.questions ?? vue.refusModification;
  const [questions, setQuestions] = useState<QuestionEditee[]>(() =>
    devoir.questions.length > 0
      ? devoir.questions.map((question) => ({
          cle: prochaineCle++,
          enonce: question.enonce,
          explication: question.explication,
          reponses: question.reponses.map((reponse) => ({ texte: reponse.texte, correcte: reponse.correcte })),
        }))
      : [questionVide()],
  );

  const changer = (rang: number, modification: (question: QuestionEditee) => QuestionEditee) =>
    setQuestions((courantes) => courantes.map((question, index) => (index === rang ? modification(question) : question)));
  const deplacer = (rang: number, sens: -1 | 1) =>
    setQuestions((courantes) => {
      const suite = [...courantes];
      const [question] = suite.splice(rang, 1);
      suite.splice(rang + sens, 0, question!);
      return suite;
    });

  const enregistrer = async () => {
    const envoi: SaisieQuestion[] = questions.map((question) => ({
      enonce: question.enonce.trim(),
      explication: question.explication?.trim() || null,
      reponses: question.reponses.map((reponse) => ({ texte: reponse.texte.trim(), correcte: reponse.correcte })),
    }));
    if (await executer({ geste: 'questions', devoirId: devoir.id, questions: envoi }, 'Questions enregistrées')) onFermer();
  };

  return (
    <div className="space-y-4">
      <div className="flex min-w-0 items-center gap-2">
        <button type="button" onClick={onFermer} className={ACTION_ICONE} aria-label="Retour aux évaluations">
          <ChevronLeft className="h-5 w-5" aria-hidden />
        </button>
        <div className="min-w-0">
          <div className="text-xs font-semibold uppercase text-codialis-gray">Questions du quiz</div>
          <div className="font-display font-bold text-codialis-navy">{devoir.titre}</div>
        </div>
      </div>
      {figees && (
        <p className="rounded-xl bg-amber-50 p-3 text-sm text-amber-900" role="note">
          {figees}
        </p>
      )}
      <ol className="space-y-4">
        {questions.map((question, rang) => (
          <li key={question.cle} className="rounded-2xl border border-gray-100 p-3 sm:p-4">
            <fieldset disabled={Boolean(figees)} className="space-y-3">
              <legend className="sr-only">Question {rang + 1}</legend>
              <div className="flex items-center gap-2">
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-codialis-mint/15 text-sm font-bold text-codialis-teal">
                  {rang + 1}
                </span>
                <span className="min-w-0 flex-1 text-xs font-semibold uppercase text-codialis-gray">QCM · une bonne réponse</span>
                <button
                  type="button"
                  className={ACTION_ICONE}
                  aria-label={`Monter la question ${rang + 1}`}
                  disabled={rang === 0}
                  onClick={() => deplacer(rang, -1)}
                >
                  <ArrowUp className="h-4 w-4" aria-hidden />
                </button>
                <button
                  type="button"
                  className={ACTION_ICONE}
                  aria-label={`Descendre la question ${rang + 1}`}
                  disabled={rang === questions.length - 1}
                  onClick={() => deplacer(rang, 1)}
                >
                  <ArrowDown className="h-4 w-4" aria-hidden />
                </button>
                <button
                  type="button"
                  className={ACTION_ICONE}
                  aria-label={`Retirer la question ${rang + 1}`}
                  onClick={() => setQuestions((courantes) => courantes.filter((_, index) => index !== rang))}
                >
                  <Trash2 className="h-4 w-4" aria-hidden />
                </button>
              </div>
              <div>
                <label htmlFor={`enonce-${question.cle}`} className={CLASSE_LIBELLE}>
                  Énoncé
                </label>
                <textarea
                  id={`enonce-${question.cle}`}
                  rows={2}
                  required
                  maxLength={limites.enonceMax}
                  value={question.enonce}
                  onChange={(evenement) => changer(rang, (courante) => ({ ...courante, enonce: evenement.target.value }))}
                  className={CLASSE_CHAMP}
                />
              </div>
              <div role="radiogroup" aria-label={`Bonne réponse de la question ${rang + 1}`} className="space-y-2">
                {question.reponses.map((reponse, rangReponse) => (
                  <div key={rangReponse} className="flex min-w-0 items-center gap-2">
                    <input
                      type="radio"
                      name={`bonne-${question.cle}`}
                      checked={reponse.correcte}
                      aria-label={`Réponse ${String.fromCharCode(65 + rangReponse)} : la bonne`}
                      onChange={() =>
                        changer(rang, (courante) => ({
                          ...courante,
                          reponses: courante.reponses.map((une, index) => ({ ...une, correcte: index === rangReponse })),
                        }))
                      }
                      className="h-4 w-4 shrink-0 accent-codialis-mint"
                    />
                    <span className="w-5 shrink-0 text-center text-sm font-bold text-codialis-navy">
                      {String.fromCharCode(65 + rangReponse)}
                    </span>
                    <input
                      aria-label={`Texte de la réponse ${String.fromCharCode(65 + rangReponse)}`}
                      required
                      maxLength={limites.reponseMax}
                      value={reponse.texte}
                      onChange={(evenement) =>
                        changer(rang, (courante) => ({
                          ...courante,
                          reponses: courante.reponses.map((une, index) =>
                            index === rangReponse ? { ...une, texte: evenement.target.value } : une,
                          ),
                        }))
                      }
                      className={`${CLASSE_CHAMP} flex-1`}
                    />
                    <button
                      type="button"
                      className={ACTION_ICONE}
                      aria-label={`Retirer la réponse ${String.fromCharCode(65 + rangReponse)}`}
                      disabled={question.reponses.length <= limites.reponsesMin}
                      onClick={() =>
                        changer(rang, (courante) => {
                          const reponses = courante.reponses.filter((_, index) => index !== rangReponse);
                          // La bonne réponse retirée, la première la remplace : il en faut toujours une.
                          return {
                            ...courante,
                            reponses: reponses.some((une) => une.correcte)
                              ? reponses
                              : reponses.map((une, index) => ({ ...une, correcte: index === 0 })),
                          };
                        })
                      }
                    >
                      <X className="h-4 w-4" aria-hidden />
                    </button>
                  </div>
                ))}
              </div>
              {question.reponses.length < limites.reponsesMax && (
                <button
                  type="button"
                  className={ACTION_SECONDAIRE}
                  onClick={() =>
                    changer(rang, (courante) => ({ ...courante, reponses: [...courante.reponses, { texte: '', correcte: false }] }))
                  }
                >
                  <Plus className="h-3.5 w-3.5" aria-hidden />
                  Ajouter une réponse
                </button>
              )}
              <div>
                <label htmlFor={`explication-${question.cle}`} className={CLASSE_LIBELLE}>
                  Explication <span className="font-normal normal-case text-codialis-gray">(montrée après la réponse)</span>
                </label>
                <textarea
                  id={`explication-${question.cle}`}
                  rows={2}
                  maxLength={limites.explicationMax}
                  value={question.explication ?? ''}
                  onChange={(evenement) => changer(rang, (courante) => ({ ...courante, explication: evenement.target.value }))}
                  className={CLASSE_CHAMP}
                />
              </div>
            </fieldset>
          </li>
        ))}
      </ol>
      {!figees && (
        <div className="flex flex-wrap items-center justify-between gap-2">
          <button
            type="button"
            className={ACTION_SECONDAIRE}
            disabled={questions.length >= limites.questionsMax}
            onClick={() => setQuestions((courantes) => [...courantes, questionVide()])}
          >
            <Plus className="h-3.5 w-3.5" aria-hidden />
            Ajouter une question
          </button>
          <div className="flex flex-wrap gap-2">
            <button type="button" onClick={onFermer} className={BOUTON_SECONDAIRE}>
              Annuler
            </button>
            <button type="button" disabled={enCours} onClick={() => void enregistrer()} className={BOUTON_PRINCIPAL}>
              Enregistrer les questions
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Résultats
// ---------------------------------------------------------------------------

const ETATS: Record<EtatResultat, { texte: string; ton: 'success' | 'info' | 'neutral' }> = {
  A_FAIRE: { texte: 'À faire', ton: 'neutral' },
  EN_COURS: { texte: 'En cours', ton: 'info' },
  FAIT: { texte: 'Fait', ton: 'success' },
  RENDU: { texte: 'Rendu', ton: 'success' },
  CORRIGE: { texte: 'Corrigé', ton: 'success' },
};

function ResultatsDuDevoir({ coursId, devoir }: { coursId: string; devoir: DevoirDuCours }): JSX.Element {
  const { data, isLoading, error } = useResultatsDevoir(coursId, devoir.id);
  if (isLoading) return <Chargement libelle="Chargement des résultats…" />;
  if (error || !data) return <MessageErreur message={messageErreur(error, 'Les résultats n’ont pas pu être chargés.')} />;
  const quiz = data.devoir.type === 'QUIZ';
  const tuiles = [
    { libelle: 'Concernés', valeur: String(data.synthese.concernes) },
    { libelle: quiz ? 'Faits' : 'Rendus', valeur: String(data.synthese.faits) },
    ...(quiz ? [{ libelle: 'En cours', valeur: String(data.synthese.enCours) }] : []),
    { libelle: 'En retard', valeur: String(data.synthese.enRetard) },
    { libelle: 'Moyenne', valeur: data.synthese.moyenne === null ? '—' : `${noteLisible(data.synthese.moyenne)}/20` },
  ];
  return (
    <div className="space-y-4">
      {!data.visible && (
        <p className="rounded-xl bg-amber-50 p-3 text-sm text-amber-900" role="note">
          Les apprenants ne voient pas encore ce {quiz ? 'quiz' : 'devoir'} : publiez-le, et le cours aussi.
        </p>
      )}
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-5">
        {tuiles.map((tuile) => (
          <div key={tuile.libelle} className="rounded-xl bg-gray-50 p-3">
            <div className="text-[11px] uppercase text-codialis-gray">{tuile.libelle}</div>
            <div className="font-display text-lg font-bold text-codialis-navy">{tuile.valeur}</div>
          </div>
        ))}
      </div>
      {data.lignes.length === 0 ? (
        <p className="text-sm text-codialis-gray">Aucun apprenant en formation ne reçoit encore ce {quiz ? 'quiz' : 'devoir'}.</p>
      ) : (
        <ul className="divide-y divide-gray-100 rounded-xl border border-gray-100">
          {data.lignes.map((ligne) => (
            <li key={ligne.apprenantId} className="flex flex-wrap items-center gap-x-3 gap-y-1 px-3 py-2.5 text-sm">
              <span className="min-w-0 flex-1 basis-40 font-medium text-codialis-navy">
                {ligne.prenom} {ligne.nom}
                <span className="ml-1.5 text-xs font-normal text-codialis-gray">{ligne.site}</span>
              </span>
              <Pastille texte={ETATS[ligne.etat].texte} ton={ETATS[ligne.etat].ton} />
              {ligne.enRetard && <Pastille texte="En retard" ton="warning" />}
              {ligne.note !== null && (
                <span className="font-semibold text-codialis-navy">
                  {noteLisible(ligne.note)}/20{' '}
                  <span className="text-xs font-normal text-codialis-gray">
                    {[ligne.bonnes !== null ? `${ligne.bonnes}/${data.devoir.questions}` : null, ligne.notePubliee ? null : 'note auto']
                      .filter(Boolean)
                      .join(' · ')
                      .replace(/^(.+)$/, '($1)')}
                  </span>
                </span>
              )}
              {!quiz && ligne.depots > 1 && <span className="text-xs text-codialis-gray">{ligne.depots} dépôts</span>}
              {ligne.le && <span className="text-xs text-codialis-gray">{dateLocale(ligne.le)}</span>}
            </li>
          ))}
        </ul>
      )}
      <p className="text-xs text-codialis-gray">
        {quiz ? 'Les quiz faits' : 'Les travaux rendus'} se corrigent dans{' '}
        <Link to="/instructor-grading" className="font-semibold text-codialis-sky hover:underline">
          « Corrections »
        </Link>
        {quiz ? ' : la note automatique y est validée ou ajustée.' : '.'}
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Liste
// ---------------------------------------------------------------------------

function CarteDevoir({
  devoir,
  lectureSeule,
  coursPublie,
  enCours,
  onModifier,
  onQuestions,
  onResultats,
  onPublication,
  onSupprimer,
}: {
  devoir: DevoirDuCours;
  lectureSeule: boolean;
  coursPublie: boolean;
  enCours: boolean;
  onModifier: () => void;
  onQuestions: () => void;
  onResultats: () => void;
  onPublication: () => void;
  onSupprimer: () => void;
}): JSX.Element {
  const quiz = devoir.type === 'QUIZ';
  const Icone = ICONES[quiz ? 'QUIZ' : 'DEVOIR'];
  const publie = devoir.publieLe !== null;
  const refusPublication = publie ? devoir.refus.retrait : devoir.refus.publication;
  return (
    <li className="rounded-2xl border border-gray-100 p-3 sm:p-4">
      <div className="flex min-w-0 items-start gap-3">
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-codialis-sky/10 text-codialis-sky">
          <Icone className="h-4 w-4" aria-hidden />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-semibold text-codialis-navy">{devoir.titre}</span>
            <Pastille texte={devoir.typeLibelle} ton="info" />
            <Pastille texte={publie ? 'Publié' : 'Brouillon'} ton={publie ? 'success' : 'neutral'} />
          </div>
          <div className="mt-1 text-xs text-codialis-gray">
            Avant le {echeanceLongue(devoir.echeance)}
            {quiz && ` · ${devoir.questions.length} question${devoir.questions.length > 1 ? 's' : ''} · ${devoir.dureeMinutes} min`}
            {` · ${devoir.bloc.libelle}`}
          </div>
          {publie && !coursPublie && (
            <div className="mt-1 text-xs text-amber-800">Visible des apprenants à la publication du cours.</div>
          )}
          {devoir.commences > 0 && (
            <div className="mt-1 text-xs text-codialis-navy">
              {quiz
                ? `${devoir.rendus} fait${devoir.rendus > 1 ? 's' : ''}, ${devoir.commences} commencé${devoir.commences > 1 ? 's' : ''}`
                : `${devoir.rendus} rendu${devoir.rendus > 1 ? 's' : ''}`}
            </div>
          )}
        </div>
      </div>
      <div className="mt-3 flex flex-wrap items-center gap-2">
        {quiz && (
          <button type="button" onClick={onQuestions} className={ACTION_SECONDAIRE}>
            <ListChecks className="h-3.5 w-3.5" aria-hidden />
            {lectureSeule || devoir.refus.questions ? 'Voir les questions' : 'Questions'}
          </button>
        )}
        {!lectureSeule && (
          <>
            <button type="button" onClick={onModifier} className={ACTION_SECONDAIRE}>
              <Pencil className="h-3.5 w-3.5" aria-hidden />
              Modifier
            </button>
            <button
              type="button"
              onClick={onPublication}
              disabled={enCours || refusPublication !== null}
              title={refusPublication ?? undefined}
              className={publie ? ACTION_SECONDAIRE : ACTION_PRINCIPALE}
            >
              {publie ? <EyeOff className="h-3.5 w-3.5" aria-hidden /> : <Eye className="h-3.5 w-3.5" aria-hidden />}
              {publie ? 'Retirer de la publication' : 'Publier'}
            </button>
          </>
        )}
        <button type="button" onClick={onResultats} className={ACTION_SECONDAIRE}>
          <BarChart3 className="h-3.5 w-3.5" aria-hidden />
          Résultats
        </button>
        {!lectureSeule && (
          <button
            type="button"
            onClick={onSupprimer}
            disabled={enCours || devoir.refus.suppression !== null}
            title={devoir.refus.suppression ?? 'Supprimer'}
            aria-label={`Supprimer « ${devoir.titre} »`}
            className={`${ACTION_ICONE} ml-auto`}
          >
            <Trash2 className="h-4 w-4" aria-hidden />
          </button>
        )}
      </div>
      {!lectureSeule && refusPublication && (
        <p className="mt-2 text-xs text-codialis-gray">{refusPublication}</p>
      )}
    </li>
  );
}

export function EvaluationsCours({ cours }: { cours: Cours }): JSX.Element {
  const toast = useToast();
  const { data: vue, isLoading, error } = useDevoirsCours(cours.id);
  const gestes = useGesteDevoir(cours.id);
  const [edition, setEdition] = useState<Edition | null>(null);
  const [questions, setQuestions] = useState<string | null>(null);
  const [resultats, setResultats] = useState<DevoirDuCours | null>(null);
  const [aSupprimer, setASupprimer] = useState<DevoirDuCours | null>(null);

  const groupes = useMemo(() => {
    if (!vue) return [];
    const parModule = vue.modules.map((module, rang) => ({
      cle: module.id,
      titre: `Module ${rang + 1} · ${module.titre}`,
      devoirs: vue.devoirs.filter((devoir) => devoir.module?.id === module.id),
    }));
    return [...parModule, { cle: 'fin', titre: 'Fin du cours', devoirs: vue.devoirs.filter((devoir) => devoir.module === null) }].filter(
      (groupe) => groupe.devoirs.length > 0,
    );
  }, [vue]);

  if (isLoading) return <Chargement libelle="Chargement des évaluations…" />;
  if (error || !vue) return <MessageErreur message={messageErreur(error, 'Les évaluations n’ont pas pu être chargées.')} />;

  const lectureSeule = vue.refusModification !== null;
  const executer = async (geste: GesteDevoir, succes: string): Promise<boolean> => {
    try {
      await gestes.mutateAsync(geste);
      toast(succes);
      return true;
    } catch (erreur) {
      toast(messageErreur(erreur, 'Enregistrement impossible. Réessayez.'));
      return false;
    }
  };

  const enEdition = questions ? vue.devoirs.find((devoir) => devoir.id === questions) : undefined;
  if (enEdition) {
    return (
      <EditeurQuiz
        key={enEdition.id}
        devoir={enEdition}
        vue={vue}
        onFermer={() => setQuestions(null)}
        executer={executer}
        enCours={gestes.isPending}
      />
    );
  }

  return (
    <div className="space-y-4">
      <p className="text-sm text-codialis-gray">
        Quiz et devoirs à rendre du cours, rangés par module. Chacun naît en brouillon ; publié, l’apprenant le retrouve dans
        son module dès que le cours est publié.{' '}
        {vue.formateur
          ? `Les dépôts sont signalés à ${vue.formateur}.`
          : 'Les dépôts sont signalés au formateur référent de chaque apprenant.'}
      </p>
      {!lectureSeule && (
        <div className="flex flex-wrap gap-2">
          <button type="button" onClick={() => setEdition({ mode: 'creer', quiz: true })} className={ACTION_PRINCIPALE}>
            <Plus className="h-3.5 w-3.5" aria-hidden />
            Ajouter un quiz
          </button>
          <button type="button" onClick={() => setEdition({ mode: 'creer', quiz: false })} className={ACTION_SECONDAIRE}>
            <Plus className="h-3.5 w-3.5" aria-hidden />
            Ajouter un devoir à rendre
          </button>
        </div>
      )}
      {groupes.length === 0 ? (
        <p className="rounded-2xl bg-gray-50 p-4 text-sm text-codialis-gray">Aucun quiz ni devoir dans ce cours pour l’instant.</p>
      ) : (
        groupes.map((groupe) => (
          <section key={groupe.cle} aria-label={groupe.titre}>
            <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-codialis-gray">{groupe.titre}</h3>
            <ul className="space-y-3">
              {groupe.devoirs.map((devoir) => (
                <CarteDevoir
                  key={devoir.id}
                  devoir={devoir}
                  lectureSeule={lectureSeule}
                  coursPublie={vue.statutCours === 'PUBLIE'}
                  enCours={gestes.isPending}
                  onModifier={() => setEdition({ mode: 'modifier', devoir })}
                  onQuestions={() => setQuestions(devoir.id)}
                  onResultats={() => setResultats(devoir)}
                  onPublication={() =>
                    void executer(
                      { geste: 'publication', devoirId: devoir.id, publication: devoir.publieLe ? 'RETIRER' : 'PUBLIER' },
                      devoir.publieLe ? 'Retiré de la publication' : 'Publié',
                    )
                  }
                  onSupprimer={() => setASupprimer(devoir)}
                />
              ))}
            </ul>
          </section>
        ))
      )}

      <Modale
        titre={
          edition?.mode === 'modifier'
            ? `Modifier « ${edition.devoir.titre} »`
            : edition?.quiz
              ? 'Ajouter un quiz'
              : 'Ajouter un devoir à rendre'
        }
        ouverte={edition !== null}
        onFermer={() => setEdition(null)}
      >
        {edition && (
          <FormulaireDevoir
            key={edition.mode === 'modifier' ? edition.devoir.id : String(edition.quiz)}
            vue={vue}
            edition={edition}
            onFermer={() => setEdition(null)}
            executer={executer}
            enCours={gestes.isPending}
          />
        )}
      </Modale>

      <Modale
        titre={resultats ? `Résultats · ${resultats.titre}` : 'Résultats'}
        ouverte={resultats !== null}
        onFermer={() => setResultats(null)}
        largeur="max-w-3xl"
      >
        {resultats && (
          <div className="p-4 sm:p-6">
            <ResultatsDuDevoir coursId={cours.id} devoir={resultats} />
          </div>
        )}
      </Modale>

      <Modale titre="Supprimer ce devoir ?" ouverte={aSupprimer !== null} onFermer={() => setASupprimer(null)} largeur="max-w-md">
        <div className="p-4 sm:p-6">
          <p className="text-sm text-codialis-gray">
            « {aSupprimer?.titre} »{aSupprimer?.type === 'QUIZ' ? ' et ses questions seront supprimés.' : ' sera supprimé.'}
          </p>
          <div className="mt-5 flex flex-wrap justify-end gap-2">
            <button type="button" onClick={() => setASupprimer(null)} className={BOUTON_SECONDAIRE}>
              Annuler
            </button>
            <button
              type="button"
              disabled={gestes.isPending}
              onClick={() =>
                aSupprimer &&
                void executer({ geste: 'supprimer', devoirId: aSupprimer.id }, 'Devoir supprimé').then((fait) => fait && setASupprimer(null))
              }
              className="min-h-[44px] rounded-xl bg-red-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-red-700 disabled:opacity-60"
            >
              Supprimer
            </button>
          </div>
        </div>
      </Modale>
    </div>
  );
}
